import React, { useEffect, useMemo, useRef, useCallback } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import Checkbox from '@mui/material/Checkbox'
import { CircularProgress, Link, Chip, Box, Select, MenuItem, FormControl, ListItemText, OutlinedInput } from '@mui/material'
import { ExpandMore, ExpandLess, LinkedIn, Language } from '@mui/icons-material'

// Third-party Imports
import classnames from 'classnames'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table'

// Component Imports
import TablePaginationComponent from '../molecules/TablePaginationComponent'

// Style Imports
import tableStyles from '../../styles/table.module.scss'

// Column Definitions
const columnHelper = createColumnHelper()

const getFirstWebsite = (web) => {
  if (!web) return null

  const websites = web.split(/[,;]/).map(w => w.trim()).filter(w => w)

  if (websites.length === 0) return null
  const firstWebsite = websites[0]

  return firstWebsite.indexOf('://') === -1 ? `http://${firstWebsite}` : firstWebsite
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 450
    }
  }
}

const FirmContactTable = (props) => {
  const {
    data,
    contactSelection,
    setContactSelection,
    fetching,
    fuzzyFilter,
    globalFilter,
    setGlobalFilter,
    pageSize,
    disableInteraction = false,
    isSearching = false
  } = props
  const { t } = useTranslation()

  const skipPageResetRef = useRef()

  const getSelectedContactIds = useCallback((firmIdentifier, contacts) => {
    return contacts
      .filter(contact => contactSelection[`${firmIdentifier}_${contact.id}`])
      .map(contact => contact.id)
  }, [contactSelection])

  const handleContactSelectChange = useCallback((firmIdentifier, contacts, selectedIds) => {
    if (disableInteraction) return

    skipPageResetRef.current = true

    const newSelection = { ...contactSelection }

    contacts.forEach(contact => {
      delete newSelection[`${firmIdentifier}_${contact.id}`]
    })

    selectedIds.forEach(contactId => {
      newSelection[`${firmIdentifier}_${contactId}`] = true
    })

    setContactSelection(newSelection)
  }, [disableInteraction, contactSelection, setContactSelection])

  const firmSelectionCounts = useMemo(() => {
    const counts = {}
    data.forEach(firm => {
      const selectedCount = (firm.contacts || []).filter(contact => {
        const contactKey = `${firm.identifier}_${contact.id}`
        return contactSelection[contactKey]
      }).length

      counts[firm.identifier] = {
        selected: selectedCount,
        total: (firm.contacts || []).length
      }
    })
    return counts
  }, [data, contactSelection])

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('mailTemplate.employeeSelection.firmName'),
        size: 300,
        minSize: 300,
        cell: ({ row }) => {
          const firm = row.original
          const website = getFirstWebsite(firm.web)
          const counts = firmSelectionCounts[firm.identifier] || { selected: 0, total: 0 }

          return (
            <div className="flex items-center gap-4" style={{ width: '100%', minWidth: '200px' }}>
              <div className="flex flex-col" style={{ flex: 1 }}>
                <Typography color="text.primary" className="font-medium">
                  {website
                    ? (
                      <Link
                        href={website}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        underline="hover"
                        sx={{
                          color: 'inherit !important',
                          '&:hover': { color: 'inherit !important' }
                        }}
                      >
                        {firm.name}
                      </Link>
                      )
                    : firm.name}
                </Typography>
                {firm.contacts && (
                  <Typography variant="caption" color="text.secondary">
                    {firm.contacts.length} {t('mailTemplate.employeeSelection.contacts')}
                  </Typography>
                )}
              </div>
              {counts.selected > 0 && (
                <Chip
                  label={`${counts.selected} ${t('mailTemplate.employeeSelection.selected')}`}
                  size="small"
                  color="primary"
                />
              )}
            </div>
          )
        }
      }),
      {
        id: 'contactSelect',
        header: t('mailTemplate.employeeSelection.selectEmployees'),
        size: 400,
        minSize: 350,
        cell: ({ row }) => {
          const firm = row.original
          const contacts = firm.contacts || []
          const firmIdentifier = firm.identifier
          const selectedIds = getSelectedContactIds(firmIdentifier, contacts)
          const hasNoContacts = contacts.length === 0

          if (hasNoContacts && !isSearching) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t('mailTemplate.employeeSelection.noData')}
              </Typography>
            )
          }

          const isSelectDisabled = disableInteraction || (isSearching && hasNoContacts)

          return (
            <FormControl sx={{ width: '100%', maxWidth: 450 }} size="small">
              <Select
                multiple
                value={selectedIds}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.includes('__select_all__')) {
                    const allSelected = contacts.length > 0 && selectedIds.length === contacts.length
                    if (allSelected) {
                      handleContactSelectChange(firmIdentifier, contacts, [])
                    } else {
                      handleContactSelectChange(firmIdentifier, contacts, contacts.map(c => c.id))
                    }
                  } else {
                    handleContactSelectChange(firmIdentifier, contacts, value)
                  }
                }}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (isSearching && hasNoContacts) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <em>{t('mailTemplate.employeeSelection.searchingContacts')}</em>
                      </Box>
                    )
                  }
                  if (selected.length === 0) {
                    return <em>{t('mailTemplate.employeeSelection.selectEmployeesPlaceholder')}</em>
                  }
                  const selectedItems = selected.map(id => {
                    const contact = contacts.find(c => c.id === id)

                    if (!contact) return null

                    const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim()

                    const isUnknown = !name
                    const displayName = name || contact.emailAddress || t('mailTemplate.employeeSelection.unknownContact')

                    return { id, displayName, isUnknown }
                  }).filter(Boolean)
                  return (
                    <span>
                      {selectedItems.map((item, index) => (
                        <span key={item.id}>
                          {item.isUnknown ? <em>{item.displayName}</em> : item.displayName}
                          {index < selectedItems.length - 1 && ', '}
                        </span>
                      ))}
                    </span>
                  )
                }}
                MenuProps={MenuProps}
                disabled={isSelectDisabled}
                displayEmpty
              >
                {contacts.length > 0 && (
                  <MenuItem
                    value="__select_all__"
                    sx={{ py: 1.5, borderBottom: '1px solid #e0e0e0' }}
                  >
                    <Checkbox
                      checked={contacts.length > 0 && selectedIds.length === contacts.length}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < contacts.length}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {t('mailTemplate.employeeSelection.selectAll')}
                        </Typography>
                      }
                    />
                  </MenuItem>
                )}
                {contacts.map((contact) => {
                  const firstName = contact.firstName || ''
                  const lastName = contact.lastName || ''

                  const nameFromParts = `${firstName} ${lastName}`.trim()
                  const isUnknownContact = !nameFromParts

                  const fullName = nameFromParts || contact.emailAddress || t('mailTemplate.employeeSelection.unknownContact')
                  const position = contact.position || ''
                  const sourcePage = contact.sourcePage || ''
                  const isLinkedIn = sourcePage.toLowerCase().includes('linkedin.com')

                  return (
                    <MenuItem key={contact.id} value={contact.id} sx={{ py: 1.5 }}>
                      <Checkbox checked={selectedIds.includes(contact.id)} size="small" sx={{ flexShrink: 0 }} />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ fontStyle: isUnknownContact ? 'italic' : 'normal', flexShrink: 0 }}
                            >
                              {fullName}
                            </Typography>
                            {sourcePage && (
                              <Link
                                href={sourcePage}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                              >
                                {isLinkedIn
                                  ? <LinkedIn sx={{ fontSize: 18, color: '#0A66C2' }} />
                                  : <Language sx={{ fontSize: 18, color: 'text.secondary' }} />}
                              </Link>
                            )}
                            {position && (
                              <Chip
                                label={position}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderRadius: 1,
                                  height: 20,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          )
        }
      }
    ],
    [t, disableInteraction, isSearching, firmSelectionCounts, getSelectedContactIds, handleContactSelectChange]
  )

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter
    },
    state: {
      globalFilter
    },
    initialState: {
      pagination: {
        pageSize: 10
      }
    },
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    autoResetPageIndex: !skipPageResetRef.current,
    getRowId: (row) => `firm_${row.identifier}`
  })

  useEffect(() => {
    skipPageResetRef.current = false
  })

  useEffect(() => {
    table.setPageSize(pageSize)
  }, [pageSize, table])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="overflow-x-auto" style={{ flex: 1 }}>
          <table className={tableStyles.table} style={{ tableLayout: 'fixed', width: '100%', whiteSpace: 'normal' }}>
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '60%' }} />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : (
                          <div
                            className={classnames({
                              'flex items-center': header.column.getIsSorted(),
                              'cursor-pointer select-none': header.column.getCanSort()
                            })}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <ExpandLess />,
                              desc: <ExpandMore />
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getFilteredRowModel().rows.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={table.getVisibleFlatColumns().length}
                      className="text-center"
                    >
                      {fetching
                        ? <CircularProgress />
                        : t('mailTemplate.employeeSelection.noData')}
                    </td>
                  </tr>
                  )
                : (
                    table.getRowModel().rows.map((row) => {
                      return (
                        <tr
                          key={row.id}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      )
                    })
                  )}
            </tbody>
          </table>
        </div>

        <div style={{ flexShrink: 0, marginTop: 'auto' }}>
          <TablePagination
            component={() => (
              <TablePaginationComponent
                table={table}
                infoText1={t('mailTemplate.employeeSelection.tableInfo_1')}
                infoText2={t('mailTemplate.employeeSelection.tableInfo_2')}
              />
            )}
            count={table.getFilteredRowModel().rows.length}
            rowsPerPage={table.getState().pagination.pageSize}
            page={table.getState().pagination.pageIndex}
            onPageChange={(_, page) => {
              table.setPageIndex(page)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default FirmContactTable
